import { FloatingPositionAdapter } from "../adapters/FloatingPositionAdapter";
import type { CmsMenuItem } from "../types";

type SelectHandler = (item: CmsMenuItem, trigger: HTMLElement) => void;

let menuSeq = 0;

export class CmsOverflowMenu {
    readonly #menu: HTMLElement;
    #cleanup: (() => void) | null = null;
    #trigger: HTMLElement | null = null;
    #items: CmsMenuItem[] = [];
    #handler: SelectHandler | null = null;
    #activeIndex = 0;

    constructor(private readonly className = "admin-cms-overflow-menu") {
        this.#menu = document.createElement("div");
        this.#menu.className = className;
        this.#menu.id = `cms-overflow-menu-${++menuSeq}`;
        this.#menu.hidden = true;
        this.#menu.setAttribute("role", "menu");
        document.body.appendChild(this.#menu);
        this.#bindGlobal();
    }

    show(trigger: HTMLElement, items: CmsMenuItem[], handler: SelectHandler): void {
        this.hide(false);
        this.#trigger = trigger;
        this.#items = items.filter((item) => item.intent !== "noop");
        this.#handler = handler;
        this.#activeIndex = Math.max(0, this.#items.findIndex((item) => !item.disabled));
        this.#render();
        trigger.setAttribute("aria-expanded", "true");
        trigger.setAttribute("aria-controls", this.#menu.id);
        this.#menu.hidden = false;
        this.#cleanup = FloatingPositionAdapter.auto(trigger, this.#menu, {
            placement: "bottom-end",
            offset: 6,
            shiftPadding: 10,
        });
        this.#focusActive();
    }

    toggle(trigger: HTMLElement, items: CmsMenuItem[], handler: SelectHandler): void {
        if (this.#trigger === trigger && !this.#menu.hidden) {
            this.hide();
            return;
        }
        this.show(trigger, items, handler);
    }

    hide(returnFocus = true): void {
        this.#cleanup?.();
        this.#cleanup = null;
        this.#menu.hidden = true;
        this.#menu.innerHTML = "";
        const trigger = this.#trigger;
        trigger?.setAttribute("aria-expanded", "false");
        trigger?.removeAttribute("aria-controls");
        this.#trigger = null;
        this.#handler = null;
        this.#items = [];
        if (returnFocus) trigger?.focus({ preventScroll: true });
    }

    destroy(): void {
        this.hide(false);
        this.#menu.remove();
    }

    #render(): void {
        this.#menu.innerHTML = "";
        this.#items.forEach((item, index) => {
            const button = document.createElement("button");
            button.type = "button";
            button.className = "admin-cms-overflow-menu__item";
            if (item.danger) button.classList.add("admin-cms-overflow-menu__item--danger");
            button.setAttribute("role", "menuitem");
            button.tabIndex = index === this.#activeIndex ? 0 : -1;
            button.disabled = !!item.disabled;
            button.dataset.cmsOverflowIndex = String(index);
            button.innerHTML = `${item.icon ? `<i class="${this.#escape(item.icon)}" aria-hidden="true"></i>` : ""}<span>${this.#escape(item.label)}</span>`;
            button.addEventListener("click", (event) => {
                event.preventDefault();
                this.#select(index);
            });
            this.#menu.appendChild(button);
        });
    }

    #bindGlobal(): void {
        document.addEventListener("pointerdown", (event) => {
            if (this.#menu.hidden) return;
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (this.#menu.contains(target) || this.#trigger?.contains(target)) return;
            this.hide(false);
        }, true);

        document.addEventListener("keydown", (event) => {
            if (this.#menu.hidden) return;
            if (event.key === "Escape") {
                event.preventDefault();
                this.hide();
                return;
            }
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                this.#move(event.key === "ArrowDown" ? 1 : -1);
                return;
            }
            if (event.key === "Home" || event.key === "End") {
                event.preventDefault();
                this.#activeIndex = event.key === "Home" ? 0 : this.#items.length - 1;
                this.#focusActive();
                return;
            }
            if (event.key === "Enter" || event.key === " ") {
                const active = document.activeElement;
                if (active instanceof HTMLElement && this.#menu.contains(active)) {
                    event.preventDefault();
                    const index = parseInt(active.dataset.cmsOverflowIndex ?? "-1", 10);
                    this.#select(index);
                }
            }
        });
    }

    #move(delta: number): void {
        if (this.#items.length === 0) return;
        let next = this.#activeIndex;
        for (let i = 0; i < this.#items.length; i++) {
            next = (next + delta + this.#items.length) % this.#items.length;
            if (!this.#items[next].disabled) break;
        }
        this.#activeIndex = next;
        this.#focusActive();
    }

    #focusActive(): void {
        const buttons = Array.from(this.#menu.querySelectorAll<HTMLButtonElement>("[role='menuitem']"));
        buttons.forEach((button, index) => {
            button.tabIndex = index === this.#activeIndex ? 0 : -1;
        });
        buttons[this.#activeIndex]?.focus({ preventScroll: true });
    }

    #select(index: number): void {
        const item = this.#items[index];
        const trigger = this.#trigger;
        const handler = this.#handler;
        if (!item || item.disabled || !trigger || !handler) return;
        this.hide(false);
        handler(item, trigger);
    }

    #escape(value: string): string {
        return value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
    }
}
